import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useIsMobile } from '../../custom-hooks/useIsMobile';
import {
    Modal,
    Slide,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Button,
    Alert,
    Box,
    Typography,
    Divider,
    CircularProgress,
    Rating,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import ModeRoundedIcon from '@mui/icons-material/ModeRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import StoreRoundedIcon from '@mui/icons-material/StoreRounded';
import {
    ChevronDown,
    UserRoundCog,
    Search,
    Store,
    UserRoundPlus,
} from 'lucide-react';
import { convertToTitleCase } from '../../orgViews/OrgUtils';
import { StyleLinks } from '../../orgViews/OrgCommonComponents';
import {
    JobberFullProfileDetails,
    JobberAvalTimeWindows,
    CreateJobberWorkerTypeReqDTO,
    SkillLevel,
    PreferredCommunicationMethod,
    CreateOrgToAgencyObjReqDTO,
    AgencyObjClient,
    CreateOrgForAgencyFormData,
    OrgLocationClient,
    UpdateDefaultWorkerTypePayRateRequestDTO,
    CreateJobberWorkerTypeDTO,
} from '../agency-data/AgencyInterfaces';
import {
    GetAllJobberAvailbilities,
    FullyHireJobberAPICall,
    AddJobberWorkerTypeToJobber,
    CreateOrgOnAgency,
    GetAgencyWTokenOnly,
    RegisterBulkJobbersPOSTAPICall,
    UpdateDefaultWorkerTypePayRate,
    CreateNewJobberWorkerTypePOSTAPICall,
} from '../agency-api-calls/AgencyAPI';
import {
    GetJobberOnboardingTemplatesAPICall,
} from '../trustvault/jobberonboarding/JobberOnboardingTemplateAPI';
import { buildEditJobberOnboardingTemplatePath } from '../trustvault/jobberonboarding/JobberOnboardingTemplatePaths';
import {
    WorkTaskTemplateResponseDTO,
    formatWorkTaskTemplateCreatorDisplay,
} from '../trustvault/worktasks/WorkTaskTemplateInterfaces';
import {
    WorkforceTagBuilder,
    copyToClipboard,
    CrudMyWorkerTypes,
} from './MyAgencyWorkforce';
import {
    OrgJobberMatchingType,
    AddressDto,
    JobberWorkerTypeAgencyPrivateViewDTO,
    LaborSalesTaxRuleDTO,
    OrgHourlyRateMarkupType,
    ServicedCity,
    BillingTimeInterval,
} from '../../common/CommonInterfaces';
import { filterActiveJobberWorkerTypes } from '../agency-data/JobberWorkerTypeUtils';
import {
    calculateOrgBillingRate,
    calculateOrgBillingRateFormData,
    determineDefaultOrgBillingPayRate,
    GeneratePayloadForCreateOrgToAgencyObjReqDTO,
    parseEmailsFromString,
    validateBillingCycleAnchorFormFields,
} from './AgencyActionUtils';
import { formatDateOnlyFromEpochEastern, epochSecFromDateInputEasternStartOfDay } from '../../direct-invoicing/utils-methods/DirectInvoicingEasternDateUtils';
import { ToolTipCommonComponent } from '../../common/CommonComponents';
import {
  BILLING_CYCLE_START_DATE_LABEL,
  CLIENT_FACING_BILLING_TOOLTIPS,
} from '../agency-data/AgencyBillingClientTooltips';
import {
  FACILITY_WORK_JURISDICTION_LABEL,
  CLIENT_FACING_FACILITY_JURISDICTION_TOOLTIPS,
} from '../agency-data/AgencyFacilityJurisdictionClientTooltips';
import { WorkJurisdictionIdentifier } from '../../orgViews/orgConstants';
import { DEFAULT_COMMON_ADDRESS } from '../../common/CommonDataConstants';
import { WorkyContactButton } from '../agency-components/AgencyComponentSetOne';
import { WorkyNavigation } from '../agency-components/WorkyNavigation';

// CSS Imports
import './agencyactions.css';
import './agencybilling2.css';
import '../../orgViews/orgworkforcejobbermodal.css';
import '../../worky-leads/pages/workysaleshome.css';
import { CrudWorkTaskTemplates } from './CrudWorkTaskTemplates';
import { isValidString } from '../../common/CommonUtilsMethods';
import { WorkyNumberInput } from '../../common/WorkyNumberInput';
import { WorkySelectComponent } from '../../shared-components/worky-select-component/WorkySelectComponent';
import {
  LaborSalesTaxRulesEditor,
  LaborSalesTaxRulesEditorHandle,
} from '../agency-components/LaborSalesTaxRulesEditor';
import {
  WgcFormFieldLabel,
  getEmptyWhenZeroNumberInputValue,
  parseNumberInputValue,
} from '../agency-components/WgcFormFieldLabel';
import { WgcFormFieldRequirement } from '../agency-components/WgcFormFieldLabelInterfaces';
import {
  ADD_WORKER_TYPE_FORM_LEGEND,
  CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS,
  MASTER_WORKER_PAY_RATE_OVERRIDE_LABEL,
  RATE_MARKUP_TYPE_LABEL,
} from '../agency-data/AgencyOrgWorkerTypeClientTooltips';

/** @deprecated Prefer CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS — kept for existing form field props. */
const WORKER_TYPE_TOOLTIPS = {
  workerTypeName: CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.workerTypeName,
  defaultJobberPayRate: CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.defaultJobberPayRate,
  defaultOrgBillingPayRate: CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.defaultOrgBillingPayRate,
  getMarkupTypeTooltipText: CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.getMarkupTypeTooltipText,
};


const PageHeader = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className={isMobile ? 'payments-header-holder-mobile' : 'payments-header-holder'}>
      <div className='payments-header'>
        <div className='payments-header-title-holder'>
          <button id="payments-header-active" className={isMobile ? 'payments-header-title-switch-mobile':'payments-header-title-switch'}>Agency Setup</button>
        </div>
        <div className='payments-header-subtitle'>View and modify your Clients & Workers</div>
      </div>
    </div>
  )
}



interface GooglePlacePrediction {
  placePrediction: {
    place: string;
    placeId: string;
    text: {
      text: string;
    };
    structuredFormat: {
      mainText: { text: string };
      secondaryText: { text: string };
    };
  };
}

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_SEARCH_API_KEY;

const fetchPlacePredictions = async (input: string): Promise<GooglePlacePrediction[]> => {
  if (!input || input.length < 3 || !GOOGLE_PLACES_API_KEY) return [];
  try {
    const response = await axios.post(
      'https://places.googleapis.com/v1/places:autocomplete',
      {
        input,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
        },
      }
    );
    return response.data.suggestions || [];
  } catch (error) {
    console.error('Error fetching place predictions:', error);
    return [];
  }
};

const fetchPlaceDetailsAndMap = async (placeId: string): Promise<any> => {
  if (!GOOGLE_PLACES_API_KEY) return null;
  try {
    const response = await axios.get(
      `https://places.googleapis.com/v1/places/${placeId}`,
      {
        params: {
          fields: 'addressComponents,location',
        },
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_PLACES_API_KEY,
          'X-Goog-FieldMask': 'addressComponents'
        }
      }
    );

    const components = response.data.addressComponents;
    const addr: any = {
      street: '',
      unitNumber: '',
      city: '',
      province: '',
      postalCode: '',
      country: ''
    };

    let streetNumber = '';
    let route = '';

    components.forEach((component: any) => {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.longText;
      if (types.includes('route')) route = component.longText;
      if (types.includes('subpremise')) addr.unitNumber = component.longText;
      if (types.includes('locality')) addr.city = component.longText;
      if (!addr.city && types.includes('sublocality_level_1')) addr.city = component.longText;
      if (types.includes('administrative_area_level_1')) addr.province = component.longText;
      if (types.includes('postal_code')) addr.postalCode = component.longText;
      if (types.includes('country')) addr.country = component.longText;
    });

    addr.street = `${streetNumber} ${route}`.trim();
    return addr;

  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};

export const AddressFacilityForm = ({
  locationAddress,
  onAddressChange,
  onKeyDown,
  headerValue,
  customStyle,
  onGooglePlaceSelectionChange,
}: {
  locationAddress?: {
    street?: string;
    unitNumber?: string;
    city?: string;
    province?: string;
    postalCode?: string;
    country?: string;
    majorIntersection?: string;
    entranceInstructions?: string;
  };
  onAddressChange: (updatedAddress: any) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  headerValue: string;
  customStyle?: React.CSSProperties;
  /**
   * Fires false when the street text changes, and true after a Google place is chosen.
   */
  onGooglePlaceSelectionChange?: (googlePlaceSelected: boolean) => void;
}) => {
  const [suggestions, setSuggestions] = useState<GooglePlacePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleFieldChange = (field: string, value: string) => {
    const updatedLocation = {
      ...locationAddress,
      [field]: value
    };
    onAddressChange(updatedLocation);
    if (field === 'street') {
        setShowSuggestions(true);
        onGooglePlaceSelectionChange?.(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationAddress?.street && locationAddress.street.length > 3 && showSuggestions) {
        fetchPlacePredictions(locationAddress.street).then(setSuggestions);
      } else {
        setSuggestions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [locationAddress?.street, showSuggestions]);

  const handleSuggestionClick = async (placeId: string) => {
    const details = await fetchPlaceDetailsAndMap(placeId);
    if (details) {
      onAddressChange({ ...locationAddress, ...details });
      onGooglePlaceSelectionChange?.(true);
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  return (
    <div>
      <div className='modal-subtitle-mobile'>{headerValue}</div>

      <div className='modal-address-fields-container' style={{
        maxHeight: '250px',
        overflowY: 'auto',
        padding: '10px',
        border: '1px solid #eaeaea',
        borderRadius: '6px',
        marginBottom: '15px',
        ...customStyle
      }}>
        <div className='modal-input-group' style={{ position: 'relative' }}>
          <div className='modal-subtitle-mobile'>Street:</div>
          <input
            type='text'
            name='street'
            value={locationAddress?.street || ''}
            onChange={e => handleFieldChange('street', e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            className={`modal-input-mobile`}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              zIndex: 1000,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              width: '100%',
              maxHeight: '200px',
              overflowY: 'auto',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              top: '100%'
            }}>
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.placePrediction.placeId}
                  onClick={() => handleSuggestionClick(suggestion.placePrediction.placeId)}
                  style={{
                    padding: '10px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <strong>{suggestion.placePrediction.structuredFormat.mainText.text}</strong>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {suggestion.placePrediction.structuredFormat.secondaryText.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>Unit Number:</div>
          <input
            type='text'
            name='unitNumber'
            value={locationAddress?.unitNumber || ''}
            onChange={e => handleFieldChange('unitNumber', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>City:</div>
          <input
            type='text'
            name='city'
            value={locationAddress?.city || ''}
            onChange={e => handleFieldChange('city', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>Province:</div>
          <input
            type='text'
            name='province'
            value={locationAddress?.province || ''}
            onChange={e => handleFieldChange('province', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>Postal Code:</div>
          <input
            type='text'
            name='postalCode'
            value={locationAddress?.postalCode || ''}
            onChange={e => handleFieldChange('postalCode', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>Country:</div>
          <input
            type='text'
            name='country'
            value={locationAddress?.country || ''}
            onChange={e => handleFieldChange('country', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>

        <div className='modal-input-group'>
          <div className='modal-subtitle-mobile'>Entrance Instructions(Optional):</div>
          <input
            type='text'
            name='entranceInstructions'
            value={locationAddress?.entranceInstructions || ''}
            onChange={e => handleFieldChange('entranceInstructions', e.target.value)}
            onKeyDown={onKeyDown}
            className='modal-input-mobile'
          />
        </div>
      </div>
    </div>
  );
};



const normalizeWorkerTypeWhenPercentageMarkupHidden = (
  worker: JobberWorkerTypeAgencyPrivateViewDTO | undefined,
  hidePercentageMarkupOption: boolean
): JobberWorkerTypeAgencyPrivateViewDTO | undefined => {
  if (!worker || !hidePercentageMarkupOption) {
    return worker;
  }
  if (worker.orgHourlyRateMarkupType !== OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE) {
    return worker;
  }
  const payRate: number = worker.defaultJobberPayRate ?? 0;
  const percentValue: number = worker.defaultOrgBillingPayRate ?? 0;
  const absoluteBilling: number = calculateOrgBillingRate(
    payRate,
    OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE,
    percentValue
  );
  return {
    ...worker,
    orgHourlyRateMarkupType: OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE,
    defaultOrgBillingPayRate: absoluteBilling,
  };
};

export const SetJobberWorkerTypeDetailsComponent = ({
  agencyData,
  formData,
  setFormData,
  hidePercentageMarkupOption = false,
  workerTypeOptional = false,
  showMasterWorkerPayRateOverride = false,
}: {
  agencyData: AgencyObjClient;
  formData: CreateOrgForAgencyFormData;
  setFormData: (formData: CreateOrgForAgencyFormData) => void;
  readonly hidePercentageMarkupOption?: boolean;
  readonly workerTypeOptional?: boolean;
  readonly showMasterWorkerPayRateOverride?: boolean;
}) => {
  const markupTypeDisplayValues = {
    [OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE]: "Percent Markup",
    [OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE]: "Default Hourly Markup",
    // [OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE]: "Hourly Markup", // limited to percentage + default hourly for now
  };

  const defaultMarkupTypeForNewSelection: OrgHourlyRateMarkupType = hidePercentageMarkupOption
    ? OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE
    : OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE;

  const emptyWorkerTypeSelection: JobberWorkerTypeAgencyPrivateViewDTO = {
    workerTypeName: '',
    workerTypeUniqueIdentifier: '',
    defaultJobberPayRate: 0,
    orgHourlyRateMarkupType: defaultMarkupTypeForNewSelection,
    defaultOrgBillingPayRate: 0,
  };

  const activeWorkerTypes: readonly JobberWorkerTypeAgencyPrivateViewDTO[] = useMemo(
    () => filterActiveJobberWorkerTypes(agencyData.jobberWorkerTypes),
    [agencyData.jobberWorkerTypes]
  );

  const workerTypeSelectValue: readonly JobberWorkerTypeAgencyPrivateViewDTO[] = useMemo(() => {
    const currentWorkerTypeName: string | undefined = formData?.workerType?.[0]?.workerTypeName;
    if (!isValidString(currentWorkerTypeName)) {
      return [];
    }
    const fromActive: JobberWorkerTypeAgencyPrivateViewDTO | undefined = activeWorkerTypes.find(
      (workerType: JobberWorkerTypeAgencyPrivateViewDTO): boolean =>
        workerType.workerTypeName === currentWorkerTypeName
    );
    if (fromActive !== undefined) {
      return [fromActive];
    }
    const fromAgency: JobberWorkerTypeAgencyPrivateViewDTO | undefined =
      agencyData.jobberWorkerTypes.find(
        (workerType: JobberWorkerTypeAgencyPrivateViewDTO): boolean =>
          workerType.workerTypeName === currentWorkerTypeName
      );
    if (fromAgency !== undefined) {
      return [fromAgency];
    }
    return formData.workerType?.[0] != null ? [formData.workerType[0]] : [];
  }, [activeWorkerTypes, agencyData.jobberWorkerTypes, formData?.workerType]);

  const handleWorkerTypeSelectChange = (
    nextWorkerTypes: readonly JobberWorkerTypeAgencyPrivateViewDTO[]
  ): void => {
    const selectedWorkerTypeRaw: JobberWorkerTypeAgencyPrivateViewDTO | undefined = nextWorkerTypes[0];
    if (selectedWorkerTypeRaw == null) {
      setFormData({
        ...formData,
        workerType: [emptyWorkerTypeSelection],
      });
      return;
    }
    const selectedWorkerType: JobberWorkerTypeAgencyPrivateViewDTO | undefined =
      normalizeWorkerTypeWhenPercentageMarkupHidden(selectedWorkerTypeRaw, hidePercentageMarkupOption);
    setFormData({
      ...formData,
      workerType: [selectedWorkerType ?? emptyWorkerTypeSelection],
    });
  };

  const updateWorkerTypeArray = (workerTypes: JobberWorkerTypeAgencyPrivateViewDTO[], updatedWorker: Partial<JobberWorkerTypeAgencyPrivateViewDTO>) => {
    const index = workerTypes.findIndex(worker => worker.workerTypeName === updatedWorker.workerTypeName);
    if (index !== -1) {
      const updatedArray = [...workerTypes];
      updatedArray[index] = { ...updatedArray[index], ...updatedWorker };
      return updatedArray;
    } else {
      return [...workerTypes, updatedWorker as JobberWorkerTypeAgencyPrivateViewDTO];
    }
  };

  const handleWorkerPayChange = (e: React.ChangeEvent<HTMLInputElement>) => {


    setFormData({
      ...formData,
      workerType: updateWorkerTypeArray(formData?.workerType, {
        workerTypeName: formData?.workerType[0]?.workerTypeName || "",
        defaultJobberPayRate: Number(e.target.value),
      }),
    });
  };

  const handleMasterWorkerPayRateOverrideChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const rawValue: string = e.target.value;
    const parsedValue: number | null = rawValue === '' ? null : Number(rawValue);
    setFormData({
      ...formData,
      workerType: updateWorkerTypeArray(formData?.workerType, {
        workerTypeName: formData?.workerType[0]?.workerTypeName || '',
        orgDefaultJobberPayRateForPayroll: parsedValue,
      }),
    });
  };

  const handleOrgMarkupTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({
      ...formData,
      workerType: updateWorkerTypeArray(formData?.workerType, {
        workerTypeName: formData?.workerType[0]?.workerTypeName || "",
        orgHourlyRateMarkupType: e.target.value as OrgHourlyRateMarkupType,
      }),
    });
  };

  const handleOrgMarkupValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      workerType: updateWorkerTypeArray(formData?.workerType || [], {
        workerTypeName: formData?.workerType?.[0]?.workerTypeName || "",
        defaultOrgBillingPayRate: Number(e.target.value),
      }),
    });
  };

  return (
    <div>
      <div className="worker-type-selection-container">
        <div className="modal-input-group">
          <ToolTipCommonComponent
            toolTipText={CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.workerTypeName}
          >
            <div className="modal-subtitle-mobile">Worker Type{workerTypeOptional ? ' (Optional)' : ''}:</div>
          </ToolTipCommonComponent>
          <WorkySelectComponent<JobberWorkerTypeAgencyPrivateViewDTO>
            options={activeWorkerTypes}
            value={workerTypeSelectValue}
            onChange={handleWorkerTypeSelectChange}
            getLabel={(workerType: JobberWorkerTypeAgencyPrivateViewDTO): string =>
              workerType.workerTypeName
            }
            getValue={(workerType: JobberWorkerTypeAgencyPrivateViewDTO): string =>
              workerType.workerTypeUniqueIdentifier
            }
            placeholder="Select Worker Type"
            enableSearch
            minSelections={0}
            maxSelections={1}
            styling={{ rootClassName: 'modal-input-dropdown-mobile' }}
          />
        </div>

        <div className="modal-input-group">
          <ToolTipCommonComponent
            toolTipText={CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.defaultJobberPayRate}
          >
            <div className="modal-subtitle-mobile">Worker Pay ($/hr):</div>
          </ToolTipCommonComponent>
          <WorkyNumberInput
            name="defaultJobberPayRate"
            value={formData?.workerType[0]?.defaultJobberPayRate || ""}
            onChange={handleWorkerPayChange}
            disabled={!formData?.workerType[0]?.workerTypeName}
            className="modal-input-mobile"
          />
        </div>

        {showMasterWorkerPayRateOverride && (
          <div className="modal-input-group">
            <ToolTipCommonComponent
              toolTipText={CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.masterWorkerPayRateOverride}
            >
              <div className="modal-subtitle-mobile">{MASTER_WORKER_PAY_RATE_OVERRIDE_LABEL} ($/hr):</div>
            </ToolTipCommonComponent>
            <WorkyNumberInput
              name="orgDefaultJobberPayRateForPayroll"
              min={0}
              step={0.01}
              value={formData?.workerType[0]?.orgDefaultJobberPayRateForPayroll ?? ''}
              onChange={handleMasterWorkerPayRateOverrideChange}
              disabled={!formData?.workerType[0]?.workerTypeName}
              className="modal-input-mobile"
              placeholder="Optional — leave blank for worker default pay"
            />
          </div>
        )}

        {!hidePercentageMarkupOption && (
          <>
            <div className="modal-input-group"></div>
            <ToolTipCommonComponent
              toolTipText={CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.getMarkupTypeTooltipText(
                formData?.workerType[0]?.orgHourlyRateMarkupType ||
                  OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE
              )}
            >
              <div className="modal-subtitle-mobile">{RATE_MARKUP_TYPE_LABEL}:</div>
            </ToolTipCommonComponent>
            <select
              name="orgHourlyRateMarkupType"
              value={
                formData?.workerType[0]?.orgHourlyRateMarkupType ||
                OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE
              }
              onChange={handleOrgMarkupTypeChange}
              className="modal-input-dropdown-mobile"
              disabled={!formData?.workerType[0]?.workerTypeName}
            >
              {Object.keys(OrgHourlyRateMarkupType)
                .filter(
                  (key) =>
                    OrgHourlyRateMarkupType[key as keyof typeof OrgHourlyRateMarkupType] !==
                    OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE
                )
                .map((key) => (
                  <option key={key} value={OrgHourlyRateMarkupType[key as keyof typeof OrgHourlyRateMarkupType]}>
                    {markupTypeDisplayValues[OrgHourlyRateMarkupType[key as keyof typeof OrgHourlyRateMarkupType]]}
                  </option>
                ))}
            </select>
          </>
        )}
      </div>

      {!hidePercentageMarkupOption &&
        formData?.workerType[0]?.orgHourlyRateMarkupType ===
          OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE && (
          <div className="modal-input-group">
            <div className="modal-subtitle-mobile">Percentage Markup:</div>
            <WorkyNumberInput
              placeholder="Percentage Markup"
              name="percentageMarkupValue"
              value={formData?.workerType[0]?.defaultOrgBillingPayRate || ""}
              onChange={handleOrgMarkupValueChange}
              className="modal-input-mobile"
              disabled={!formData?.workerType[0]?.workerTypeName}
            />
          </div>
        )}

      {formData?.workerType[0]?.orgHourlyRateMarkupType === OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE && (
        <div className="modal-input-group">
          <div className="modal-subtitle-mobile">Default Hourly Rate Markup:</div>
          <WorkyNumberInput
            placeholder="Default Hourly Rate Markup"
            name="defaultOrgBillingPayRate"
            value={formData?.workerType[0]?.defaultOrgBillingPayRate || ""}
            onChange={handleOrgMarkupValueChange}
            className="modal-input-mobile"
            disabled={!formData?.workerType[0]?.workerTypeName}
          />
        </div>
      )}

      {/* {formData?.workerType[0]?.orgHourlyRateMarkupType === OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE && (
        <div className="modal-input-group">
          <div className="modal-subtitle-mobile">Hourly Rate Markup:</div>
          <input
            type="number"
            placeholder="Hourly Rate Markup"
            name="hourlyMarkupValue"
            value={formData?.workerType[0]?.defaultOrgBillingPayRate || ""}
            onChange={handleOrgMarkupValueChange}
            className="modal-input-mobile"
            disabled={!formData?.workerType[0]?.workerTypeName}
          />
        </div>
      )} */}

      <div className="modal-input-group">
        <ToolTipCommonComponent
          toolTipText={CLIENT_FACING_ORG_WORKER_TYPE_TOOLTIPS.defaultOrgBillingPayRate}
        >
          <div className="modal-subtitle-mobile">Client Billing Rate:</div>
        </ToolTipCommonComponent>
        <WorkyNumberInput
          name="defaultOrgBillingPayRate"
          value={calculateOrgBillingRateFormData(formData)}
          className="modal-input-mobile"
          readOnly
        />
      </div>
    </div>
  );
};



const CrudMyTemplates = ({ isMobile }: { isMobile: boolean }) => {
    const [templateData, setTemplateData] = useState<WorkTaskTemplateResponseDTO[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<WorkTaskTemplateResponseDTO | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const fetchTemplateData = async () => {
        try {
            const data = await GetJobberOnboardingTemplatesAPICall();
            if (data && Array.isArray(data)) {
                setTemplateData(data);
            }
        } catch (error) {
            console.error('Error fetching template data:', error);
        }
    };

    useEffect(() => {
        fetchTemplateData();
    }, []);

    const filterTemplates = (query: string) => {
        if (!query) return templateData;

        const lowerCaseQuery = query.toLowerCase();

        return templateData.filter((template) => {
            const templateName: string = template.templateName ?? '';
            const agencyName: string = template.agencyName ?? '';
            return (
                templateName.toLowerCase().includes(lowerCaseQuery) ||
                agencyName.toLowerCase().includes(lowerCaseQuery)
            );
        });
    };

    const filteredTemplates = filterTemplates(searchQuery);

    const openTemplateDetails = (template: WorkTaskTemplateResponseDTO) => {
        setSelectedTemplate(template);
        setIsModalOpen(true);
    };

    const handleEditSelectedTemplate = (): void => {
        if (!selectedTemplate) {
            return;
        }
        window.location.assign(buildEditJobberOnboardingTemplatePath(selectedTemplate.templateId));
    };

    return (
        <div className={isMobile ? 'general-info-holder-mobile-alt' : 'account-settings-crud-part-inner'}>
            {/* Search Bar */}
            <div className='crud-search-bar'>
                <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="workforce-search-input"
                />
            </div>

            {/* Templates Grid */}
            {filteredTemplates.map((template, index) => (
                <div key={index} className='account-settings-crud-item' onClick={() => openTemplateDetails(template)}>
                    <div className='account-settings-crud-item-workforce-profile-holder'>
                        <div style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <EditNoteRoundedIcon style={{ fontSize: '24px', color: '#666' }} />
                        </div>
                    </div>
                    <div className='account-settings-crud-item-workforce-jobber-holder'>
                        {template.templateName}
                    </div>
                    <div className='account-settings-crud-item-workforce-tag-holder'>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {template.agencyName}
                        </div>
                    </div>
                </div>
            ))}

            {/* Template Details Modal */}
            {selectedTemplate && isModalOpen && (
                <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                    <Slide direction={isMobile ? 'up' : 'left'} in={isModalOpen} mountOnEnter unmountOnExit>
                        <div className={isMobile ? 'agency-billing-modal-mobile' : 'agency-billing-modal'}>
                            {isMobile ? (
                                <button onClick={() => setIsModalOpen(false)} className='timeclock-close-mobile' />
                            ) : (
                                <button onClick={() => setIsModalOpen(false)} className='agency-billing-close'>
                                    <CloseRoundedIcon />
                                </button>
                            )}

                            <div className={isMobile ? 'org-jobber-workforce-modal-general-info-mobile' : 'org-jobber-workforce-modal-general-info'}>
                                <div style={{ width: '100%', textAlign: 'left' }}>
                                    <div style={{ marginBottom: '24px' }}>
                                        <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--dark-text)', margin: '0 0 8px 0', textAlign: 'left' }}>
                                            {selectedTemplate.templateName}
                                        </h2>
                                        <div style={{ fontSize: '14px', color: '#666', textAlign: 'left', marginBottom: '12px' }}>
                                            Creator: {formatWorkTaskTemplateCreatorDisplay(selectedTemplate)}
                                        </div>
                                        <button
                                            type="button"
                                            className="create-tag-btn"
                                            onClick={handleEditSelectedTemplate}
                                            style={{ padding: '8px 14px' }}
                                        >
                                            Edit template
                                        </button>
                                    </div>

                                    <div style={{ marginTop: '32px', textAlign: 'left' }}>
                                        {selectedTemplate.sections && selectedTemplate.sections.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', textAlign: 'left' }}>
                                                {selectedTemplate.sections.map((section, sectionIndex) => (
                                                    <div key={section.sectionId} className="tag-card" style={{ margin: 0, border: section.isDefaultSection ? '2px solid var(--worky-colour)' : '1px solid var(--launch-colour2)', textAlign: 'left' }}>
                                                        <div className="tag-card-header">
                                                            <div style={{ flex: 1, textAlign: 'left' }}>
                                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px', textAlign: 'left' }}>
                                                                    <h3 className="tag-name" style={{ fontSize: '18px', margin: 0, textAlign: 'left' }}>
                                                                        {section.sectionTitle}
                                                                    </h3>
                                                                    {section.isDefaultSection && (
                                                                        <span style={{ padding: '4px 12px', backgroundColor: 'var(--worky-colour)', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
                                      Default
                                    </span>
                                                                    )}
                                                                </div>
                                                                {section.sectionDescription && (
                                                                    <p style={{ fontSize: '14px', color: '#666', margin: '0 0 12px 0', textAlign: 'left' }}>
                                                                        {section.sectionDescription}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px', textAlign: 'left' }}>
                                                            {section.questions.map((question, qIndex) => (
                                                                <div key={question.questionId || qIndex} style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #e0e0e0', textAlign: 'left' }}>
                                                                    <div style={{ marginBottom: '8px', textAlign: 'left' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--dark-text)' }}>
                                      {qIndex + 1}. {question.question}
                                        {question.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                                    </span>
                                                                    </div>
                                                                    <div style={{
                                                                        fontSize: '14px',
                                                                        color: 'var(--text-colour)',
                                                                        marginBottom: '8px',
                                                                        display: 'flex',
                                                                        gap: '16px',
                                                                        flexWrap: 'wrap',
                                                                        textAlign: 'left'
                                                                    }}>
                                                                        <span style={{ fontWeight: '600' }}>Type:</span>
                                                                        <span>
                                    {String(question.questionType) === 'TEXT_FIELD' || question.questionType === 0 ? 'Text Field' :
                                        String(question.questionType) === 'FILE_TYPE' || question.questionType === 1 ? 'File Upload' :
                                            String(question.questionType) === 'FILE_TYPE_WITH_EXPIRY' ? 'File Upload with Expiry' :
                                                String(question.questionType) === 'DROP_DOWN_SELECT' ? 'Dropdown' :
                                                    String(question.questionType)}
                                  </span>
                                                                        <span style={{ fontWeight: '600' }}>Required:</span> <span>{question.required ? 'Yes' : 'No'}</span>
                                                                    </div>
                                                                    {question.description && (
                                                                        <div style={{
                                                                            fontSize: '14px',
                                                                            color: 'var(--text-colour)',
                                                                            marginTop: '8px',
                                                                            padding: '12px',
                                                                            backgroundColor: 'var(--launch-colour2)',
                                                                            borderRadius: '8px',
                                                                            borderLeft: '4px solid var(--worky-colour)',
                                                                            textAlign: 'left'
                                                                        }}>
                                                                            <strong>Description:</strong> {question.description}
                                                                        </div>
                                                                    )}
                                                                    {(question as any).showSectionsWhen && (question as any).showSectionsWhen.length > 0 && (
                                                                        <div style={{
                                                                            fontSize: '14px',
                                                                            color: 'var(--text-colour)',
                                                                            marginTop: '8px',
                                                                            padding: '12px',
                                                                            backgroundColor: '#fef3c7',
                                                                            borderRadius: '8px',
                                                                            borderLeft: '4px solid #f59e0b',
                                                                            textAlign: 'left'
                                                                        }}>
                                                                            <strong>Conditional Logic:</strong> Shows sections when conditions are met
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={{ padding: '20px', textAlign: 'left', color: '#666' }}>
                                                No questions or sections found in this template.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Slide>
                </Modal>
            )}
        </div>
    );
};






const WorkyActions = ({
                          isMobile,
                          setModifyWorkersModalOpen,
                          modalModifyWorkersParamater,
                          setModifyWorkersModalParamater,
                          setModalModifyOrgsOpen,
                          modalModifyOrgsParamater,
                          setModalModifyOrgsParamater,
                          agencyData
                      }: {
    isMobile: boolean,
    setModifyWorkersModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
    modalModifyWorkersParamater: any,
    setModifyWorkersModalParamater: React.Dispatch<React.SetStateAction<any>>,
    setModalModifyOrgsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    modalModifyOrgsParamater: any,
    setModalModifyOrgsParamater: React.Dispatch<React.SetStateAction<any>>,
    agencyData: AgencyObjClient
}) => {

    const handleOpenWorkersModal = () => setModifyWorkersModalOpen(true);
    const handleOpenOrgsModal = () => setModalModifyOrgsOpen(true);




  const DesktopActions = () => {
    return (
      <>
        <div className='agency-actions-crud-holder'>
          {/* <div className='account-settings-crud-part-holder'>
              <div className='account-settings-crud-part-title'>
                My Workers
                <button onClick={handleOpenWorkersModal} className='account-settings-crud-part-title-btn'>
                  <EditNoteRoundedIcon sx={{ fontSize: "20px" }} /> Add Workers
                </button>
              </div>
              <CrudMyWorkers isMobile={isMobile}/>
            </div> */}

        </div>
      </>
    );
  }

  const MobileActions = () => {
    return (
      <div className='actions-holder-mobile'>
        {/* <div className='profile-settings-title-mobile-alt'>
            My Workers
            <button onClick={handleOpenWorkersModal} className='account-settings-crud-part-title-btn-mobile'>
              <EditNoteRoundedIcon sx={{ fontSize: "20px" }} />
            </button>
          </div> */}
        {/* <div className='general-settings-container-mobile-alt'>
          <CrudMyWorkers isMobile={isMobile} />
        // </div> */}
                <div className='profile-settings-title-mobile-alt'>
                    My Worker Types
                </div>
                <div className='general-settings-container-mobile-alt'>
                    <CrudMyWorkerTypes isMobile={isMobile} agencyData={agencyData} />
                </div>

                <div className='profile-settings-title-mobile-alt'>
                    My Templates
                </div>
                <div className='general-settings-container-mobile-alt'>
                    <CrudMyTemplates isMobile={isMobile}/>
                </div>

                <div className='profile-settings-title-mobile-alt'>
                    Task Templates
                </div>
                <div className='general-settings-container-mobile-alt'>
                    <CrudWorkTaskTemplates isMobile={isMobile}/>
                </div>

            </div>
        );
    }

  return (
    <>
      {isMobile ? <MobileActions /> : <DesktopActions />}
    </>
  );
};

const getAddWorkerTypeMissingRequiredFieldMessage = (
  workerTypeName: string,
  defaultJobberPayRate: number,
  orgMarkUpValue: number | undefined
): string | null => {
  if (!isValidString(workerTypeName)) {
    return 'Enter a worker type name.';
  }
  if (!(defaultJobberPayRate > 0)) {
    return 'Enter a worker pay rate greater than 0.';
  }
  if (!(Number(orgMarkUpValue) > 0)) {
    return 'Enter a default hourly rate greater than 0.';
  }
  return null;
};

const WorkyQuickActionsAddWorkerTypeModal = ({ modalOpen, handleClose, isMobile,
  modalParamater, setModalParamater }
  : {
    modalOpen: boolean; handleClose: () => void; isMobile: boolean;
    modalParamater: CreateJobberWorkerTypeReqDTO;
    setModalParamater: React.Dispatch<React.SetStateAction<CreateJobberWorkerTypeReqDTO>>;
  }) => {
  const { enqueueSnackbar } = useSnackbar();
  const taxEditorRef = useRef<LaborSalesTaxRulesEditorHandle>(null);
  const [taxValidationError, setTaxValidationError] = useState<string | null>(null);
  const handleTaxValidationErrorChange = useCallback((validationError: string | null): void => {
    setTaxValidationError((currentValidationError: string | null): string | null =>
      currentValidationError === validationError ? currentValidationError : validationError
    );
  }, []);
  const showSuccessMessage = (message: string): void => {
    enqueueSnackbar(message, { variant: 'success' });
  };
  const handleInputChangeForMarkupInput = (changeEvent: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const rawMarkupValue: string = changeEvent.target.value;
    setModalParamater(prevState => ({
      ...prevState,
      orgMarkUpValue: parseNumberInputValue(rawMarkupValue)
    }));
  };

  const handleInputChangeForWorkerTypeName = (changeEvent: React.ChangeEvent<HTMLInputElement>): void => {
    const workerTypeName: string = changeEvent.target.value;
    setModalParamater(prevState => ({
      ...prevState,
      workerTypeName
    }));
  }

  const handleInputChangeForDefaultWorkerPay = (changeEvent: React.ChangeEvent<HTMLInputElement>): void => {
    const rawPayRate: string = changeEvent.target.value;
    setModalParamater(prevState => ({
      ...prevState,
      defaultJobberPayRate: parseNumberInputValue(rawPayRate)
    }));
  }

  const handleInputChangeForDescription = (changeEvent: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const description: string = changeEvent.target.value;
    setModalParamater(prevState => ({
      ...prevState,
      description
    }));
  }

  useEffect(() => {
    setModalParamater(prevState => ({
      ...prevState,
      orgHourlyRateMarkupType: OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE,
      defaultOrgBillingPayRate: calculateOrgBillingRate(
        prevState.defaultJobberPayRate,
        OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE,
        prevState.orgMarkUpValue
      )
    }));
  }, [modalParamater.defaultJobberPayRate, modalParamater.orgMarkUpValue]);

  useEffect((): void => {
    if (!modalOpen) {
      setTaxValidationError(null);
    }
  }, [modalOpen]);

  const missingRequiredFieldMessage: string | null = getAddWorkerTypeMissingRequiredFieldMessage(
    modalParamater.workerTypeName,
    Number(modalParamater.defaultJobberPayRate),
    modalParamater.orgMarkUpValue
  );
  const isSubmitDisabled: boolean = missingRequiredFieldMessage !== null || taxValidationError !== null;

  const handleSubmit = async (): Promise<void> => {
    if (missingRequiredFieldMessage !== null) {
      enqueueSnackbar(missingRequiredFieldMessage, { variant: 'warning' });
      return;
    }

    let laborSalesTaxRules: LaborSalesTaxRuleDTO[] = [];
    try {
      laborSalesTaxRules = taxEditorRef.current?.buildRules() ?? [];
    } catch {
      return;
    }

    const finalSubmission: CreateJobberWorkerTypeReqDTO = {
      workerTypeName: modalParamater.workerTypeName,
      defaultJobberPayRate: modalParamater.defaultJobberPayRate,
      orgHourlyRateMarkupType: OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE,
      defaultOrgBillingPayRate: modalParamater.orgMarkUpValue,
      jobberSkillQualificationsRequirements: modalParamater.jobberSkillQualificationsRequirements || {},
      description: modalParamater.description,
      laborSalesTaxRules,
    };
    console.log("finalSubmission ", finalSubmission)

    try {
      await CreateNewJobberWorkerTypePOSTAPICall(finalSubmission);

      handleClose();
      showSuccessMessage("Worker type created successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Error occurred while creating a new jobber worker type:", error);
      enqueueSnackbar('Failed to create worker type. Please try again.', { variant: 'error' });
    }
  }



  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="add-worker-type-modal-title"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className="wgc-modal add-worker-type-modal"
        style={{ outline: 'none' }}
      >
        <div className="wgc-modal-header">
          <div>
            <div className="wgc-modal-title" id="add-worker-type-modal-title">
              Add Worker Type
            </div>
            <div className="wgc-modal-subtitle">
              Create a worker type with worker pay and a fixed hourly rate billed to the client.
            </div>
          </div>
          <button className="wgc-modal-close" onClick={handleClose}>
            <CloseRoundedIcon />
          </button>
        </div>

        <div className="wgc-modal-body">
          <p className="wgc-form-legend">{ADD_WORKER_TYPE_FORM_LEGEND}</p>
          <div className="wgc-form-group">
            <WgcFormFieldLabel
              label="Worker Type"
              requirement={WgcFormFieldRequirement.REQUIRED}
              tooltipText={WORKER_TYPE_TOOLTIPS.workerTypeName}
              htmlFor="add-worker-type-name"
            />
            <input
              id="add-worker-type-name"
              className="wgc-input"
              placeholder="Enter worker type name"
              name="workerTypeName"
              value={modalParamater.workerTypeName}
              onChange={handleInputChangeForWorkerTypeName}
            />
          </div>

          <div className="wgc-form-group">
            <WgcFormFieldLabel
              label="Description"
              requirement={WgcFormFieldRequirement.OPTIONAL}
              htmlFor="add-worker-type-description"
            />
            <textarea
              id="add-worker-type-description"
              className="wgc-textarea"
              placeholder="Friendly description of the role and its details"
              name="description"
              value={modalParamater.description || ''}
              onChange={handleInputChangeForDescription}
              rows={3}
            />
          </div>

          <div className="wgc-form-group">
            <WgcFormFieldLabel
              label="Worker Pay"
              requirement={WgcFormFieldRequirement.REQUIRED}
              tooltipText={WORKER_TYPE_TOOLTIPS.defaultJobberPayRate}
              htmlFor="add-worker-type-pay-rate"
            />
            <WorkyNumberInput
              id="add-worker-type-pay-rate"
              className="wgc-input"
              min={0}
              step={0.01}
              placeholder="Enter worker pay rate"
              name="defaultJobberPayRate"
              value={getEmptyWhenZeroNumberInputValue(Number(modalParamater.defaultJobberPayRate))}
              onChange={handleInputChangeForDefaultWorkerPay}
            />
          </div>

          <div className="wgc-form-group">
            <WgcFormFieldLabel
              label="Default Hourly Rate"
              requirement={WgcFormFieldRequirement.REQUIRED}
              tooltipText={WORKER_TYPE_TOOLTIPS.defaultOrgBillingPayRate}
              htmlFor="add-worker-type-markup-value"
            />
            <WorkyNumberInput
              id="add-worker-type-markup-value"
              className="wgc-input"
              min={0}
              step={0.01}
              placeholder="Enter default hourly rate"
              name="percentageMarkupValue"
              value={getEmptyWhenZeroNumberInputValue(modalParamater.orgMarkUpValue)}
              onChange={handleInputChangeForMarkupInput}
            />
          </div>

          <LaborSalesTaxRulesEditor
            key={modalOpen ? 'add-worker-type-tax-open' : 'add-worker-type-tax-closed'}
            ref={taxEditorRef}
            initialRules={null}
            helperNote="Choose a province, then set sales tax for this worker type. This is saved together with the pay rate."
            isSaving={false}
            hideActionButtons
            onValidationErrorChange={handleTaxValidationErrorChange}
          />
        </div>

        <div className="wgc-modal-footer">
          <button className="wgc-action-btn" onClick={handleClose}>
            Cancel
          </button>
          <button className="wgc-action-btn primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
            Submit
          </button>
        </div>
      </div>
    </Modal>
  );
};







const WorkyQuickActions2 = (
  {
    setWorkerModalOpen,
    modalWorkerParamater,
    setWorkerModalParamater
  }:
    {
      setWorkerModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
      modalWorkerParamater: CreateJobberWorkerTypeReqDTO;
      setWorkerModalParamater: React.Dispatch<React.SetStateAction<CreateJobberWorkerTypeReqDTO>>;
    }
) => {

  return (
    <div className='actions-holder'>

      <button className='worky-actions-btn-v2' onClick={() => setWorkerModalOpen(true)} >
        <UserRoundPlus size={21} /> Add Worker Type
      </button>


    </div>
  );
};








const WorkyBulkAddWorkersModal = ({ isMobile, modalOpen, handleClose }: { isMobile: boolean, modalOpen: boolean, handleClose: () => void }) => {
    const [workers, setWorkers] = useState<string[]>([]);
    const [bulkInput, setBulkInput] = useState('');
    const [manualInput, setManualInput] = useState({ email: '' });
    const { enqueueSnackbar } = useSnackbar();
    const [templates, setTemplates] = useState<WorkTaskTemplateResponseDTO[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');


    const fetchTemplates = async () => {
        try {
            const templatesData = await GetJobberOnboardingTemplatesAPICall();
            if (templatesData) {
                setTemplates(templatesData);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    useEffect(() => {
        if (modalOpen) {
            fetchTemplates();
        }
    }, [modalOpen]);


    const showSuccessMessage = (message: string) => {
        enqueueSnackbar(message, { variant: 'success' });
    };
    const handleBulkJobbersSave = async () => {
        try {
            const response = await RegisterBulkJobbersPOSTAPICall(workers.map(rawEmail => ({ jobberEmailAddress: rawEmail, templateId: selectedTemplateId || undefined })));
            console.log("Bulk jobbers saved successfully:", response);
            setWorkers([]); // Clear the workers list after successful save
            setSelectedTemplateId('');


            handleClose(); // Close the modal
            showSuccessMessage("Bulk jobbers saved successfully!");
        } catch (error) {
            console.error("Error saving bulk jobbers:", error);
        }
    };

  const parseBulkInput = () => {

    const workerListFound: string[] = parseEmailsFromString(bulkInput)
    setWorkers(workerListFound)
    setBulkInput('');
  };


  const removeWorker = (index: number) => {
    const updatedWorkers = workers.filter((_, i) => i !== index);
    setWorkers(updatedWorkers);
  };

  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Slide direction={isMobile ? 'up' : 'left'} in={modalOpen} mountOnEnter unmountOnExit>
        <div className={isMobile ? 'actions-modal-mobile' : 'actions-modal'}>
          {isMobile ? (
            <button onClick={handleClose} className='timeclock-close-mobile' />
          ) : (
            <button onClick={handleClose} className='agency-billing-close'><CloseRoundedIcon /></button>
          )}

          <div className='modal-input-group-holder'>
            <div className='modal-title-alt-mobile'>
              My Workers
              <button className='modal-worker-search-expand-btn'>
                <UnfoldMoreRoundedIcon sx={{ rotate: "37.5deg" }} />
              </button>
            </div>

            <div className='modal-input-group'>
              <div className='modal-worker-group'>
                <input type='text' className='modal-worker-search' placeholder='Search workers...' />
                <div className='modal-worker'></div>
                <div className='modal-worker'></div>
                <div className='modal-worker'></div>
                <div className='modal-worker'></div>
                <div className='modal-worker'></div>
              </div>
            </div>

            <div className='modal-title-alt-mobile'>
              Bulk Add Workers
              <button className='modal-worker-search-expand-btn'>
                <UnfoldMoreRoundedIcon sx={{ rotate: "37.5deg" }} />
              </button>
            </div>
            <div className='modal-subtitle-alt-mobile'>Add all your workers, by pasting their emails here</div>
            <div className='modal-subtitle-alt-mobile'>To add user emails, paste emails in any format in the below text field</div>

            <div className='modal-input-group-alt-2'>
              <textarea
                className='modal-worker-textarea'
                placeholder='Enter worker emails...'
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
              ></textarea>
              <button className='modal-worker-add-btn' onClick={parseBulkInput}> <EastRoundedIcon /> </button>
            </div>

            {workers?.length > 0 ? <div className='modal-subtitle-alt-2-mobile'>Added Workers</div> : null}
            <div className='modal-input-group-workers'>
              {workers.map((worker, index) => (
                <div className='modal-added-workers' key={index}>
                  <div className='modal-added-workers-info'>
                    {worker}
                  </div>
                  <button className='modal-added-workers-btn' onClick={() => removeWorker(index)}>
                    <CloseRoundedIcon />
                  </button>
                </div>
              ))}
            </div>

                        <div className='modal-input-group'>
                            <div className='modal-subtitle-mobile'>Onboarding Template (Optional)</div>
                            <select
                                value={selectedTemplateId}
                                onChange={(e) => setSelectedTemplateId(e.target.value)}
                                className='modal-input-dropdown-mobile'
                                style={{ width: '100%' }}
                            >
                                <option value="">No template selected</option>
                                {templates.map((template) => (
                                    <option key={template.templateId} value={template.templateId}>
                                        {template.templateName}
                                    </option>
                                ))}
                            </select>
                        </div>


                        <button
                            className='modal-submit-btn-mobile'
                            onClick={handleBulkJobbersSave}
                            disabled={workers.length === 0}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </Slide>
        </Modal>
    );
};

const WorkyBulkAddOrgModal = ({ isMobile, modalOpen, handleClose }: { isMobile: boolean, modalOpen: boolean, handleClose: () => void }) => {
  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Slide direction={isMobile ? 'up' : 'left'} in={modalOpen} mountOnEnter unmountOnExit>
        <div className={isMobile ? 'actions-modal-mobile' : 'actions-modal'}>
          {isMobile ? (<button onClick={handleClose} className='timeclock-close-mobile' />) : (<button onClick={handleClose} className='agency-billing-close'><CloseRoundedIcon /></button>)}
          TESTING MY ORGS
        </div>
      </Slide>
    </Modal>
  );
};




const WorkyQuickActionsAddOrganizationModal = ({
  modalOpen,
  handleClose,
  isMobile,
  onSubmit,
  prefillData,
  agencyData
}: {
  modalOpen: boolean;
  handleClose: () => void;
  isMobile: boolean;
  onSubmit: (data: any) => void;
  prefillData?: Partial<any>;
  agencyData: AgencyObjClient,
}) => {
  const [step, setStep] = useState(1);
  const [editableFields, setEditableFields] = useState<Record<string, boolean>>({});
  const [facilities, setFacilities] = useState<OrgLocationClient[]>([]);



  const [formData, setFormData] = useState<CreateOrgForAgencyFormData>({
    orgName: prefillData?.orgName || '',
    locationAddress: DEFAULT_COMMON_ADDRESS,
    managerName: prefillData?.managerName || '',
    managerEmail: prefillData?.managerEmail || '',
    managerPhoneNum: prefillData?.managerPhoneNum || '',
    orgLocationClient: [
      {
        orgName: '',
        locationName: '',
        locationAddress: DEFAULT_COMMON_ADDRESS,
        servicedCity: ServicedCity.TORONTO,
        workJurisdictionIdentifier: WorkJurisdictionIdentifier.ONTARIO,
        orgUserContactsForLocation: []
      }
    ],
    billingInterval: prefillData?.billingInterval || '',
    billingDay: prefillData?.billingDay || '',
    billingCycleAnchorDate: prefillData?.billingCycleAnchorDate || '',
    workerType: []
    // workerType: [agencyData?.jobberWorkerTypes?.[0]],
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = e.target instanceof HTMLInputElement ? e.target.checked : false;
    
    // Handle address JSON parsing for textarea
    if (name === 'locationAddress' && e.target instanceof HTMLTextAreaElement) {
      try {
        const parsedAddress = JSON.parse(value);
        setFormData(prev => ({
          ...prev,
          [name]: parsedAddress
        }));
      } catch (error) {
        // If JSON parsing fails, keep the original address
        console.error('Failed to parse address JSON:', error);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };






  const handleFacilityInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Handle address JSON parsing for textarea
    if (name === 'locationAddress' && e.target instanceof HTMLTextAreaElement) {
      try {
        const parsedAddress = JSON.parse(value);
        setFacilities(prev =>
          prev.map((facility, i) =>
            i === index ? { ...facility, [name]: parsedAddress } : facility
          )
        );
      } catch (error) {
        // If JSON parsing fails, keep the original address
        console.error('Failed to parse facility address JSON:', error);
      }
    } else {
      setFacilities(prev =>
        prev.map((facility, i) => {
          if (i !== index) {
            return facility;
          }
          if (name === 'locationName') {
            return { ...facility, locationName: value, locationDisplayName: value };
          }
          return { ...facility, [name]: value };
        })
      );
    }
  };
  const addFacility = () => {
    const existingFacilities = facilities;
    existingFacilities.push({
      orgName: formData.orgLocationClient?.[0]?.orgName || '',
      locationName: formData.orgLocationClient?.[0]?.locationName || '',
      locationDisplayName: formData.orgLocationClient?.[0]?.locationName || '',
      locationAddress: {
        street: formData.orgLocationClient?.[0]?.locationAddress?.street || '',
        unitNumber: formData.orgLocationClient?.[0]?.locationAddress?.unitNumber || '',
        city: formData.orgLocationClient?.[0]?.locationAddress?.city || '',
        postalCode: formData.orgLocationClient?.[0]?.locationAddress?.postalCode || '',
        province: formData.orgLocationClient?.[0]?.locationAddress?.province || '',
        country: formData.orgLocationClient?.[0]?.locationAddress?.country || ''
      },
      servicedCity: formData.orgLocationClient?.[0]?.servicedCity || ServicedCity.TORONTO,
      workJurisdictionIdentifier:
        formData.orgLocationClient?.[0]?.workJurisdictionIdentifier ??
        WorkJurisdictionIdentifier.ONTARIO,
      orgUserContactsForLocation: formData.orgLocationClient?.[0]?.orgUserContactsForLocation || []
    })
    setFacilities(prev => [
      ...existingFacilities,

    ]);

    setFormData(prev => ({
      ...prev,
      orgLocationClient: existingFacilities
    }));
  };

  const removeFacility = (index: number) => {
    setFacilities(prev => prev.filter((_, i) => i !== index));
  };

  const [workerTypes, setWorkerTypes] = useState<Array<{
    workerTypeName: string;
    defaultJobberPayRate: number;
    orgHourlyRateMarkupType: OrgHourlyRateMarkupType;
    orgMarkUpValue: number;
    defaultOrgBillingPayRate: number;
  }>>([]);


  const { enqueueSnackbar } = useSnackbar();
  const showSuccessMessage = (message: string) => {
    enqueueSnackbar(message, { variant: 'success' });
  };




  const toggleEdit = (field: string) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  useEffect(() => {
    // if (modalOpen && prefillData) {
    //   setFormData(...prev, );
    // }
  }, [modalOpen, prefillData]);


  const nextStep = () => {
    console.log(`Moving from step ${step} to step ${step + 1}`, formData);
    setStep(prev => prev + 1);
  };
  const prevStep = () => {
    console.log(`Moving from step ${step} to step ${step - 1}`, formData);
    setStep(prev => prev - 1);
  };
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Email validation
  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Phone number validation
  const isValidPhone = (phone: string) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return /^1\d{10}$/.test(digitsOnly); // Starts with 1, followed by exactly 10 digits
  };
  // Step validation logic
  const validateStep = () => {
    const newErrors: Record<string, boolean> = {};

    if (step === 1) {
      if (!formData.orgName) newErrors.orgName = true;
      if (!formData.locationAddress ||
        !formData.locationAddress.city || !formData.locationAddress.country
        || !formData.locationAddress.postalCode || !formData.locationAddress.province) newErrors.locationAddress = true;
      // if (!formData.managerName) newErrors.managerName = true;
      if (formData.managerEmail && !isValidEmail(formData.managerEmail)) newErrors.managerEmail = true;
      if (formData.managerPhoneNum && !isValidPhone(formData.managerPhoneNum)) newErrors.managerPhoneNum = true;
    }

    if (step === 2) {
      if (!formData.billingInterval) newErrors.billingInterval = true;
      validateBillingCycleAnchorFormFields(formData, newErrors);
    }

    if (step === 3) {
      if (facilities.length === 0) {
        newErrors.facilities = true;
      }
      // Validate each facility in orgLocationClient
      if (formData.orgLocationClient?.length > 0) {
        formData.orgLocationClient.forEach((location, index) => {
          if (!location.locationName) {
            newErrors[`facility-${index}-locationName`] = true;
          } else if (!location.locationAddress
            || !location.locationAddress.city || !location.locationAddress.country
            || !location.locationAddress.postalCode || !location.locationAddress.province
            || !location.locationAddress?.street) {
            newErrors[`facility-${index}-street`] = true;
          } else if (!location.servicedCity) {
            newErrors[`facility-${index}-servicedCity`] = true;
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step < 5) nextStep();
    }
  };


  const formatKey = (key: string) => fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

  const formatAddress = (address: any): string => {
    if (!address || typeof address !== 'object') return '';
    const parts: string[] = [];
    if (address.street) parts.push(address.street);
    if (address.unitNumber) parts.push(`Unit ${address.unitNumber}`);
    if (address.city) parts.push(address.city);
    if (address.province) parts.push(address.province);
    if (address.postalCode) parts.push(address.postalCode);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return 'Not provided';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatBoolean = (value: boolean | string): string => {
    if (typeof value === 'string') {
      return value === 'true' || value === 'True' ? 'Yes' : 'No';
    }
    return value ? 'Yes' : 'No';
  };

  const formatBillingInterval = (interval: string): string => {
    if (!interval) return 'Not set';
    const mapping: Record<string, string> = {
      'WEEKLY': 'Weekly',
      'BI_WEEKLY': 'Bi-Weekly',
      'MONTHLY': 'Monthly',
      'BI_MONTHLY': 'Bi-Monthly'
    };
    return mapping[interval] || interval;
  };

  const fieldLabels: { [key: string]: string } = {
    orgName: 'Organization Name',
    locationAddress: 'Organization Billing Address',
    managerName: 'Organization Manager Name',
    managerEmail: 'Organization Manager Email',
    managerPhoneNum: 'Organization Manager Phone Number',
    locationName: 'Location Name',
    servicedCity: 'Serviced City',
    billingInterval: 'Billing Interval',
    billingCycleAnchorDate: BILLING_CYCLE_START_DATE_LABEL,
    workerType: 'Worker Type',

  };

  const handleFinalSubmit = async () => {


    const generatePayload = GeneratePayloadForCreateOrgToAgencyObjReqDTO(agencyData, formData)

    try {
      const response = await CreateOrgOnAgency(generatePayload);
      console.log("Organization created successfully:", response);
      handleClose();
      setStep(1);
      showSuccessMessage("Organization created successfully!");
      // Refresh the page to show the new organization
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Error creating organization:", error);
      // enqueueSnackbar("Failed to create organization. Please try again.", { variant: 'error' });
    }
  };




  const handleOrgLocationClientChange = (field: string, value: any) => {
    const newOrgLocationClient = [...(formData.orgLocationClient || [])];

    // Handle the requiresTrainedWorkers checkbox
    let fieldToUpdate = field;
    let valueToSet = value;
    if (field === 'requiresTrainedWorkers') {
      fieldToUpdate = 'orgJobberMatchingType';
      valueToSet = value ? OrgJobberMatchingType.REQUIRES_TRAINED_WORKERS_ONLY : null;
    }

    if (newOrgLocationClient.length > 0) {
      newOrgLocationClient[0] = {
        ...newOrgLocationClient[0],
        [fieldToUpdate]: valueToSet
      };
    } else {
      newOrgLocationClient.push({
        orgName: formData.orgName,
        [fieldToUpdate]: valueToSet
      });
    }

    setFormData({
      ...formData,
      orgLocationClient: newOrgLocationClient,
    });
  };

  const renderStep = ({ agencyData }: { agencyData: AgencyObjClient }) => {
    const handleOrgBillingAddressChange = (field: string, newAddressValue: AddressDto) => {
      // Only update the locationAddress in formData, don't touch orgLocationClient
      setFormData({
        ...formData,
        [field]: newAddressValue
      });

      console.log("for formData field: ", field, newAddressValue, formData)
    };

    console.log("current step ", step)
    switch (step) {
      case 1:
        return (
          <div className='modal-step-group'>

            <div>
              <div className='modal-subtitle-mobile'>Organization Name:</div>
              <input type='text' name='orgName' value={formData.orgName} onChange={handleInputChange} onKeyDown={handleKeyDown} className='modal-input-mobile' />
            </div>

            <div>
              {/* <input type='text' name='locationAddress' value={formData.locationAddress} onChange={handleInputChange} onKeyDown={handleKeyDown} className='modal-input-mobile' /> */}
              <AddressFacilityForm
                headerValue='Organization Billing Address:'
                locationAddress={formData.locationAddress}
                onAddressChange={(updatedAddress) => handleOrgBillingAddressChange("locationAddress", updatedAddress)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <div>
              <div className='modal-subtitle-mobile'>Organization Manager Name (Optional):</div>
              <input type='text' name='managerName' value={formData.managerName} onChange={handleInputChange} onKeyDown={handleKeyDown} className='modal-input-mobile' />
            </div>

            <div>
              <div className='modal-subtitle-mobile'>Organization Manager Email (Optional):</div>
              <input
                type='email'
                name='managerEmail'
                value={formData.managerEmail}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={`modal-input-mobile ${errors.managerEmail ? 'input-error' : ''}`}
              />
              {errors.managerEmail && formData.managerEmail && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  Please enter a valid email address
                </div>
              )}
            </div>

            <div>
              <div className='modal-subtitle-mobile'>Organization Manager Phone Number (Optional):</div>
              <input
                type='tel'
                name='managerPhoneNum'
                value={formData.managerPhoneNum}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setFormData(prev => ({ ...prev, managerPhoneNum: value }));
                }}
                onKeyDown={handleKeyDown}
                className={`modal-input-mobile ${errors.managerPhoneNum ? 'input-error' : ''}`}
              />
              {errors.managerPhoneNum && formData.managerPhoneNum && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  Please enter a valid phone number that starts with 1 and must be 10 digits
                </div>
              )}
            </div>

          </div>
        );
      case 2:
        return (
          <div className='modal-step-group'>            
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                <div className='modal-subtitle-mobile'>Billing Interval:</div>
                <ToolTipCommonComponent
                  toolTipText={CLIENT_FACING_BILLING_TOOLTIPS.billingInterval}
                />

              </div>
              <select
                name='billingInterval'
                value={formData.billingInterval}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className={`modal-input-dropdown-mobile ${errors.billingInterval ? 'input-error' : ''}`}
              >
                <option value=''>Select Interval</option>
                {Object.values(BillingTimeInterval).map(interval => (
                  <option key={interval} value={interval}>{interval}</option>
                ))}
              </select>
            </div>

            {(formData.billingInterval === 'WEEKLY' || formData.billingInterval === 'BI_WEEKLY') && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                  <div className='modal-subtitle-mobile'>{BILLING_CYCLE_START_DATE_LABEL}:</div>
                  <ToolTipCommonComponent
                    toolTipText={CLIENT_FACING_BILLING_TOOLTIPS.billingCycleStartDate}
                  />
                </div>
                <input
                  type='date'
                  name='billingCycleAnchorDate'
                  value={formData.billingCycleAnchorDate}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  className={`modal-input-mobile ${errors.billingCycleAnchorDate ? 'input-error' : ''}`}
                />
              </div>
            )}

          </div>
        );
      case 3:
        return (
          <div className='modal-step-group'>

            <div>
              <div className='modal-subtitle-mobile'>Location Name:</div>
              <input type='text' name='locationName' value={formData.orgLocationClient?.[0]?.locationName} onChange={e => handleOrgLocationClientChange("locationName", e.target.value)} onKeyDown={handleKeyDown} className='modal-input-mobile' />
            </div>



            <AddressFacilityForm
              headerValue='Facility Location Address'
              locationAddress={formData.orgLocationClient?.[0]?.locationAddress}
              onAddressChange={(updatedAddress) => handleOrgLocationClientChange("locationAddress", updatedAddress)}
              onKeyDown={handleKeyDown}
            />

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                <div className='modal-subtitle-mobile'>{FACILITY_WORK_JURISDICTION_LABEL}:</div>
                <ToolTipCommonComponent
                  toolTipText={CLIENT_FACING_FACILITY_JURISDICTION_TOOLTIPS.workJurisdictionIdentifier}
                />
              </div>
              <select
                name='workJurisdictionIdentifier'
                value={
                  formData.orgLocationClient?.[0]?.workJurisdictionIdentifier ??
                  WorkJurisdictionIdentifier.ONTARIO
                }
                onChange={(e) =>
                  handleOrgLocationClientChange(
                    'workJurisdictionIdentifier',
                    e.target.value as WorkJurisdictionIdentifier
                  )
                }
                onKeyDown={handleKeyDown}
                className='modal-input-dropdown-mobile'
              >
                <option value={WorkJurisdictionIdentifier.ONTARIO}>Ontario</option>
                <option value={WorkJurisdictionIdentifier.BRITISH_COLUMBIA}>British Columbia</option>
                <option value={WorkJurisdictionIdentifier.ALBERTA}>Alberta</option>
                <option value={WorkJurisdictionIdentifier.CANADA}>Canada</option>
              </select>
            </div>

            <div>
              <div className='modal-subtitle-mobile'>Serviced City:</div>
              <select
                name='servicedCity'
                value={formData.orgLocationClient?.[0]?.servicedCity}
                onChange={e => handleOrgLocationClientChange("servicedCity", e.target.value)}
                onKeyDown={handleKeyDown}
                className='modal-input-dropdown-mobile'
              >
                <option value=''>Select a city</option>
                <option value='MISSISSAUGA'>Mississauga</option>
                <option value='TORONTO'>Toronto</option>
                <option value='BRAMPTON'>Brampton</option>
                <option value='OAKVILLE'>Oakville</option>
                <option value='VAUGHAN'>Vaughan</option>
                <option value='MARKHAM'>Markham</option>
                <option value='MILTON'>Milton</option>
                <option value='REMOTE'>Remote</option>
              </select>
            </div>

            <div className='modal-input-group'>
              <Tooltip title="If this option is selected then the worker will only be able to be assigned to this location if they have been trained on this facility">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type='checkbox'
                    name='requiresTrainedWorkers'
                    checked={formData.orgLocationClient?.[0]?.orgJobberMatchingType === OrgJobberMatchingType.REQUIRES_TRAINED_WORKERS_ONLY}
                    onChange={e => handleOrgLocationClientChange("requiresTrainedWorkers", e.target.checked)}
                    onKeyDown={handleKeyDown}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label className='modal-subtitle-mobile' style={{ margin: 0, cursor: 'pointer' }}>
                    Does a Worker need to be trained to work at the specific facility?
                  </label>
                </div>
              </Tooltip>
            </div>

            <div>
              <button type="button" onClick={addFacility} disabled={!formData.orgLocationClient?.[0].locationName
                || !formData.orgLocationClient?.[0].locationAddress
                || !formData.orgLocationClient?.[0].servicedCity}
                className='modal-input-wide-btn'><AddBusinessRoundedIcon sx={{ fontSize: "20px" }} />Add Facility</button>
            </div>


            {facilities.length > 0 && (
              <div>
                <div className="modal-subtitle-mobile">Added Facilities:</div>
                <div className='facilities-group' style={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #eaeaea',
                  borderRadius: '6px',
                  padding: '5px'
                }}>
                  {facilities.map((facility, index) => (
                    <div key={index} className="facility-item">
                      <div className="facility-info">
                        <div className='facility-icon'>
                          <StoreRoundedIcon sx={{ fontSize: '18px' }} />
                        </div>
                        <strong>{facility.locationName}</strong>-
                        <div>{facility.locationAddress?.street}, {facility.locationAddress?.city}</div>-
                        <div>{facility.servicedCity}</div>
                      </div>
                      <button type="button" onClick={() => removeFacility(index)} className="facility-icon">
                        <CloseRoundedIcon sx={{ fontSize: '18px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div>
            <SetJobberWorkerTypeDetailsComponent
              agencyData={agencyData}
              formData={formData}
              setFormData={setFormData}
              hidePercentageMarkupOption
              workerTypeOptional
            />

          </div>
        )


      case 5:
        return (
          <div className='modal-step-group-review'>
            {/* Organization Info */}
            <div className="review-section">
              <div className='review-section-header'>
                <span className='review-section-title'>Organization Details</span>
              </div>
              {['orgName', 'locationAddress', 'managerName', 'managerEmail', 'managerPhoneNum'].map(key => {
                const getDisplayValue = () => {
                  if (key === 'locationAddress') {
                    return formatAddress(formData[key]);
                  } else if (key === 'managerPhoneNum') {
                    return formatPhoneNumber(formData[key] as string);
                  } 
                  return String(formData[key] || 'Not provided');
                };

                return (
                  <div key={key} className='modal-review-item'>
                    <label className='modal-review-label'>{formatKey(key)}</label>
                    <div className='modal-review-value-holder'>
                      {editableFields[key] ? (
                        typeof formData[key] === 'boolean' ? (
                          <div className='modal-toggle-switch'>
                            <div className='switch-options'>
                              <button
                                type='button'
                                className={`switch-btn ${formData[key] ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, [key]: true }))}
                              >
                                Yes
                              </button>
                              <button
                                type='button'
                                className={`switch-btn ${formData[key] === false ? 'active' : ''}`}
                                onClick={() => setFormData(prev => ({ ...prev, [key]: false }))}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        ) : key === 'locationAddress' ? (
                          <textarea
                            name={key}
                            value={JSON.stringify(formData[key], null, 2)}
                            onChange={handleInputChange}
                            className='modal-input-mobile'
                            rows={3}
                          />
                        ) : (
                          <input
                            type={key === 'managerEmail' ? 'email' : 'text'}
                            name={key}
                            value={formData[key] as string}
                            onChange={handleInputChange}
                            className='modal-input-mobile'
                          />
                        )
                      ) : (
                        <span className='modal-review-value'>{getDisplayValue()}</span>
                      )}
                      <button type='button' onClick={() => toggleEdit(key)} className='modal-edit-btn'>
                        {editableFields[key] ? <SaveRoundedIcon sx={{ fontSize: '15px' }} /> : <ModeRoundedIcon sx={{ fontSize: '15px' }} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Billing Info */}
            <div className="review-section">
              <div className='review-section-header'>
                <span className='review-section-title'>Billing Details</span>
              </div>

              {['billingInterval', 'billingCycleAnchorDate'].map(key => {
                const getDisplayValue = () => {
                  if (key === 'billingInterval') {
                    return formatBillingInterval(formData.billingInterval);
                  } else if (key === 'billingCycleAnchorDate') {
                    const epochSec: number | null = epochSecFromDateInputEasternStartOfDay(formData.billingCycleAnchorDate);
                    return epochSec !== null
                      ? formatDateOnlyFromEpochEastern(epochSec)
                      : 'Not set';
                  }
                  return String(formData[key] || 'Not set');
                };

                if (
                  key === 'billingCycleAnchorDate'
                  && formData.billingInterval !== 'WEEKLY'
                  && formData.billingInterval !== 'BI_WEEKLY'
                ) {
                  return null;
                }

                return (
                  <div key={key} className='modal-review-item'>
                    <label className='modal-review-label'>{formatKey(key)}</label>
                    <div className='modal-review-value-holder'>
                      {editableFields[key] ? (
                        key === 'billingInterval' ? (
                          <select
                            name='billingInterval'
                            value={formData.billingInterval}
                            onChange={handleInputChange}
                            className='modal-input-dropdown-mobile'
                          >
                            <option value=''>Select Interval</option>
                            <option value='WEEKLY'>Weekly</option>
                            <option value='BI_WEEKLY'>Bi-Weekly</option>
                            <option value='MONTHLY'>Monthly</option>
                          </select>
                        ) : key === 'billingCycleAnchorDate' && (formData.billingInterval === 'WEEKLY' || formData.billingInterval === 'BI_WEEKLY') ? (
                          <input
                            type='date'
                            name='billingCycleAnchorDate'
                            value={formData.billingCycleAnchorDate}
                            onChange={handleInputChange}
                            className='modal-input-mobile'
                          />
                        ) : null
                      ) : (
                        <span className='modal-review-value'>{getDisplayValue()}</span>
                      )}
                      <button type='button' onClick={() => toggleEdit(key)} className='modal-edit-btn'>
                        {editableFields[key] ? <SaveRoundedIcon sx={{ fontSize: '15px' }} /> : <ModeRoundedIcon sx={{ fontSize: '15px' }} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Facilities */}
            <div className="review-section">
              <div className='review-section-header'>
                <span className='review-section-title'>Facility Details</span>
              </div>

              {/* Saved facilities */}
              {facilities.length > 0 ? (
                facilities.map((facility, index) => {
                  const getFacilityDisplayValue = (key: string, val: any): string => {
                    if (key === 'locationAddress') {
                      return formatAddress(val);
                    } else if (key === 'servicedCity') {
                      return val ? val.charAt(0) + val.slice(1).toLowerCase().replace(/_/g, ' ') : 'Not set';
                    }
                    return String(val || 'Not provided');
                  };

                  return (
                    <div key={index} className='facility-review-item'>
                      <div className='facility-review-header'>
                        <span className='review-section-subtitle'>Facility {index + 1}</span>
                        {facility.locationName && (
                          <span className='facility-name'>{facility.locationName}</span>
                        )}
                      </div>
                      {Object.entries(facility).filter(([key]) => key !== 'orgName' && key !== 'orgUserContactsForLocation').map(([key, value]) => (
                        <div key={key} className='modal-review-item'>
                          <label className='modal-review-label'>{formatKey(key)}</label>
                          <div className='modal-review-value-holder'>
                            {editableFields[`facility-${index}-${key}`] ? (
                              key === 'servicedCity' ? (
                                <select
                                  name={key}
                                  value={value as string}
                                  onChange={(e) => handleFacilityInputChange(index, e)}
                                  className='modal-input-dropdown-mobile'
                                >
                                  <option value=''>Select a city</option>
                                  <option value='MISSISSAUGA'>Mississauga</option>
                                  <option value='TORONTO'>Toronto</option>
                                  <option value='BRAMPTON'>Brampton</option>
                                  <option value='OAKVILLE'>Oakville</option>
                                  <option value='VAUGHAN'>Vaughan</option>
                                  <option value='MARKHAM'>Markham</option>
                                  <option value='MILTON'>Milton</option>
                                  <option value='REMOTE'>Remote</option>
                                </select>
                              ) : key === 'locationAddress' ? (
                                <textarea
                                  name={key}
                                  value={JSON.stringify(value, null, 2)}
                                  onChange={(e) => handleFacilityInputChange(index, e)}
                                  className='modal-input-mobile'
                                  rows={3}
                                />
                              ) : (
                                <input
                                  type='text'
                                  name={key}
                                  value={value as string}
                                  onChange={(e) => handleFacilityInputChange(index, e)}
                                  className='modal-input-mobile'
                                />
                              )
                            ) : (
                              <span className='modal-review-value'>{getFacilityDisplayValue(key, value)}</span>
                            )}
                            <button
                              type='button'
                              onClick={() => toggleEdit(`facility-${index}-${key}`)}
                              className='modal-edit-btn'
                            >
                              {editableFields[`facility-${index}-${key}`] ?
                                <SaveRoundedIcon sx={{ fontSize: '15px' }} /> :
                                <ModeRoundedIcon sx={{ fontSize: '15px' }} />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              ) : (
                <div className='no-facilities-message'>
                  <span>No facilities added yet</span>
                </div>
              )}
            </div>

            {/* Worker Type */}
            <div className="review-section">
              <div className='review-section-header'>
                <span className='review-section-title'>Worker Details</span>
              </div>
              {formData?.workerType?.[0]?.workerTypeName ? (
                <>
                  <div className='modal-review-item'>
                    <label className='modal-review-label'>Worker Type</label>
                    <div className='modal-review-value-holder'>
                      <span className='modal-review-value'>{formData.workerType[0].workerTypeName || 'Not selected'}</span>
                    </div>
                  </div>

                  <div className='modal-review-item'>
                    <label className='modal-review-label'>Worker Pay Rate</label>
                    <div className='modal-review-value-holder'>
                      <span className='modal-review-value'>${formData.workerType[0].defaultJobberPayRate || '0'}/hr</span>
                    </div>
                  </div>

                  <div className='modal-review-item'>
                    <label className='modal-review-label'>Markup Type</label>
                    <div className='modal-review-value-holder'>
                      <span className='modal-review-value'>
                        {formData.workerType[0].orgHourlyRateMarkupType === OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE
                          ? 'Percentage Markup'
                          : formData.workerType[0].orgHourlyRateMarkupType === OrgHourlyRateMarkupType.HOURLY_RATE_MARKUP_TYPE
                            ? 'Hourly Markup'
                            : 'Default Hourly Markup'}
                      </span>
                    </div>
                  </div>

                  <div className='modal-review-item'>
                    <label className='modal-review-label'>Markup Value</label>
                    <div className='modal-review-value-holder'>
                      <span className='modal-review-value'>
                        {formData.workerType[0].orgHourlyRateMarkupType === OrgHourlyRateMarkupType.PERCENTAGE_RATE_MARKUP_TYPE
                          ? `${formData.workerType[0].defaultOrgBillingPayRate}%`
                          : `$${formData.workerType[0].defaultOrgBillingPayRate}`}
                      </span>
                    </div>
                  </div>

                  <div className='modal-review-item highlight-item'>
                    <label className='modal-review-label'>Billing Rate</label>
                    <div className='modal-review-value-holder'>
                      <span className='modal-review-value highlight-value'>${calculateOrgBillingRateFormData(formData)}/hr</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className='no-worker-type-message'>
                  <span>No worker type selected</span>
                </div>
              )}
            </div>
          </div>






        );
      default:
        return null;
    }
  };

  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Slide direction={isMobile ? 'up' : 'left'} in={modalOpen} mountOnEnter unmountOnExit>
        <div className={isMobile ? 'actions-modal-mobile' : 'actions-modal'}>
          {isMobile ? (
            <button onClick={handleClose} className='timeclock-close-mobile' />
          ) : (
            <button onClick={handleClose} className='agency-billing-close'>
              <CloseRoundedIcon />
            </button>
          )}

          <div className='modal-input-group-holder'>
            <div className='modal-title-mobile'>Add Organization</div>
            <form className='modal-input-group' onSubmit={(e) => e.preventDefault()}>
              {renderStep({ agencyData })}
              <div className='modal-navigation-buttons-group'>
                {step > 1 && <button id='modal-navigation-back' className='modal-navigation-button' type='button' onClick={prevStep}>Back</button>}
                {step < 5 && (<button id='modal-navigation-next' className='modal-navigation-button' type='button' onClick={() => { if (validateStep()) nextStep(); }}>
                  Next
                  {/* {errors ? errors:""} */}
                </button>)}
                {step === 5 && <button id='modal-navigation-next' className='modal-navigation-button' type='button' onClick={handleFinalSubmit}>Submit</button>}
              </div>
            </form>
          </div>
        </div>
      </Slide>
    </Modal>
  );
};



const AgencyActions = () => {
  const isMobile = useIsMobile();
  const [agencyData, setAgencyData] = useState<AgencyObjClient | null>(null);
  
  const fetchAgencyData = async () => {
    const agencySourceData = await GetAgencyWTokenOnly({});
    setAgencyData(agencySourceData);
  };

  useEffect(() => {
    fetchAgencyData();
  }, []);

  const [modalAddWorkerOpen, setModalAddWorkerOpen] = useState(false);
  const [modalAddWorkerParamater, setModalAddWorkerParamater] = useState<CreateJobberWorkerTypeReqDTO>({
    workerTypeName: '',
    defaultJobberPayRate: 0,
    orgHourlyRateMarkupType: OrgHourlyRateMarkupType.DEFAULT_CONFIGURED_HOURLY_RATE_MARKUP_TYPE,
    defaultOrgBillingPayRate: 0
  });

  const [modalAddOrgOpen, setModalAddOrgOpen] = useState(false);
  const [modalAddOrgParamater, setModalAddOrgParamater] = useState<any>({
    orgName: ''
  });

  const [modalModifyWorkersOpen, setModalModifyWorkersOpen] = useState(false);
  const [modalModifyWorkersParamater, setModalModifyWorkersParamater] = useState<any>({});


    const [modalModifyOrgsOpen, setModalModifyOrgsOpen] = useState(false);
    const [modalModifyOrgsParamater, setModalModifyOrgsParamater] = useState<any>({});



  return (

    <div className={isMobile ? 'org-app-v2-mobile' : 'org-app-v3'}>
      <StyleLinks />

      <WorkyNavigation />
      <div className={isMobile ? 'worky-leads-mobile-container' : 'worky-leads-desktop-container'}>
        <PageHeader isMobile={isMobile} />

                <WorkyQuickActions2
                    setWorkerModalOpen={setModalAddWorkerOpen}
                    modalWorkerParamater={modalAddWorkerParamater}
                    setWorkerModalParamater={setModalAddWorkerParamater}

                    setOrgModalOpen={setModalAddOrgOpen}
                    modalOrgParamater={modalAddOrgParamater}
                    setOrgModalParamater={setModalAddOrgParamater}
                />


        <WorkyQuickActionsAddWorkerTypeModal modalOpen={modalAddWorkerOpen} handleClose={() => setModalAddWorkerOpen(false)} isMobile={isMobile} modalParamater={modalAddWorkerParamater} setModalParamater={setModalAddWorkerParamater} />

        <WorkyQuickActionsAddOrganizationModal agencyData={agencyData} modalOpen={modalAddOrgOpen} handleClose={() => setModalAddOrgOpen(false)} isMobile={isMobile} onSubmit={(data) => setModalAddOrgParamater(data)} prefillData={modalAddOrgParamater} />

        <WorkyBulkAddWorkersModal modalOpen={modalModifyWorkersOpen} handleClose={() => setModalModifyWorkersOpen(false)} isMobile={isMobile} />

        <WorkyBulkAddOrgModal modalOpen={modalModifyOrgsOpen} handleClose={() => setModalModifyOrgsOpen(false)} isMobile={isMobile} />


        <WorkyActions
          isMobile={isMobile}

          setModifyWorkersModalOpen={setModalModifyWorkersOpen}
          modalModifyWorkersParamater={modalModifyWorkersParamater}
          setModifyWorkersModalParamater={setModalModifyWorkersParamater}

                    setModalModifyOrgsOpen={setModalModifyOrgsOpen}
                    modalModifyOrgsParamater={modalModifyOrgsParamater}
                    setModalModifyOrgsParamater={setModalModifyOrgsParamater}


                    agencyData={agencyData}
                />

        <WorkyContactButton />
      </div>
    </div>

  );
};


export { AgencyActions, WORKER_TYPE_TOOLTIPS, WorkyQuickActionsAddWorkerTypeModal, WorkyQuickActionsAddOrganizationModal }
